import React, { useMemo } from 'react';
import { Box, Typography, Paper, Grid, Chip } from '@mui/material';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  ReferenceLine,
} from 'recharts';
import { SchoolData, DistrictAgg, StatsResult, LEVEL_COLORS, getSchoolLevel } from './types';
import { CHART_COLORS, formatNumber } from '../../theme/dashboardTheme';

interface DashboardViewProps {
  filteredSchools: SchoolData[];
  districtAggregates: Map<string, DistrictAgg>;
  stats: StatsResult;
}

const LEVEL_KEYS = ['Pre-Primary', 'Primary', 'Secondary', 'Basic'];

const truncate = (text: string, maxLen: number): string =>
  text.length > maxLen ? text.slice(0, maxLen) + '...' : text;

const DashboardView: React.FC<DashboardViewProps> = ({
  filteredSchools,
  districtAggregates,
  stats,
}) => {
  // ---------- KPI helpers ----------
  const gpsCoverage = stats.total > 0 ? (stats.withGPS / stats.total) * 100 : 0;

  const ptrBg = stats.ptr > 45 ? '#FFF8E1' : '#E8F5E9';
  const ptrColor = stats.ptr > 45 ? '#6F3205' : '#1B5E20';

  const gpiBg =
    stats.gpi !== null && stats.gpi >= 0.97 && stats.gpi <= 1.03 ? '#E8F5E9' : '#FFF8E1';
  const gpiColor =
    stats.gpi !== null && stats.gpi >= 0.97 && stats.gpi <= 1.03 ? '#1B5E20' : '#6F3205';

  const gpsBg = gpsCoverage < 50 ? '#FFE5E8' : '#E8F5E9';
  const gpsColor = gpsCoverage < 50 ? '#891515' : '#1B5E20';

  // ---------- Row 2 Left: Schools by District stacked by level ----------
  const districtLevelData = useMemo(() => {
    const map = new Map<string, Record<string, number>>();
    filteredSchools.forEach((s) => {
      const districtName = s.district?.district_name || 'Unknown';
      const level = getSchoolLevel(s);
      if (!map.has(districtName)) {
        map.set(districtName, { district: districtName as any, 'Pre-Primary': 0, Primary: 0, Secondary: 0, Basic: 0 } as any);
      }
      const entry = map.get(districtName)!;
      if (level in entry) {
        (entry as any)[level] = ((entry as any)[level] || 0) + 1;
      }
    });
    return Array.from(map.values()).map((entry) => ({
      district: (entry as any).district as string,
      'Pre-Primary': (entry as any)['Pre-Primary'] as number,
      Primary: (entry as any).Primary as number,
      Secondary: (entry as any).Secondary as number,
      Basic: (entry as any).Basic as number,
    }));
  }, [filteredSchools]);

  // ---------- Row 2 Right: Level distribution pie ----------
  const levelPieData = useMemo(
    () =>
      Object.entries(stats.levels)
        .filter(([, v]) => v > 0)
        .map(([name, value]) => ({ name, value })),
    [stats.levels],
  );

  // ---------- Row 3 Left: School type pie ----------
  const typePieData = useMemo(
    () => [
      { name: 'Government', value: stats.government },
      { name: 'Private', value: stats.private },
    ],
    [stats.government, stats.private],
  );
  const typeColors = ['#1976D2', '#FF5722'];

  // ---------- Row 3 Right: Top 10 schools by enrollment ----------
  const top10Schools = useMemo(() => {
    return [...filteredSchools]
      .sort((a, b) => (b.current_enrollment || 0) - (a.current_enrollment || 0))
      .slice(0, 10)
      .map((s) => ({
        name: truncate(s.school_name, 20),
        enrollment: s.current_enrollment || 0,
        level: getSchoolLevel(s),
      }));
  }, [filteredSchools]);

  // ---------- Row 4 Left: PTR by District ----------
  const ptrByDistrict = useMemo(() => {
    return Array.from(districtAggregates.values()).map((d) => ({
      district: d.district_name,
      ptr: d.ptr,
    }));
  }, [districtAggregates]);

  // ---------- Row 4 Right: Gender enrollment by District ----------
  const genderByDistrict = useMemo(() => {
    return Array.from(districtAggregates.values()).map((d) => ({
      district: d.district_name,
      male: d.male_students,
      female: d.female_students,
    }));
  }, [districtAggregates]);

  // ---------- Render ----------
  return (
    <Box sx={{ overflowY: 'auto', height: '100%', p: 2 }}>
      {/* ===== Row 1: KPI Cards ===== */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* 1. Total Schools */}
        <Grid item xs={12} sm={6} md={2}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#E3F2FD' }}>
            <Typography variant="body2" sx={{ color: '#0D47A1', fontWeight: 500 }}>
              Total Schools
            </Typography>
            <Typography variant="h4" sx={{ color: '#0D47A1', fontWeight: 700, mt: 1 }}>
              {formatNumber(stats.total)}
            </Typography>
          </Paper>
        </Grid>

        {/* 2. Total Students */}
        <Grid item xs={12} sm={6} md={2}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#E0F2F1' }}>
            <Typography variant="body2" sx={{ color: '#004D40', fontWeight: 500 }}>
              Total Students
            </Typography>
            <Typography variant="h4" sx={{ color: '#004D40', fontWeight: 700, mt: 1 }}>
              {formatNumber(stats.totalStudents)}
            </Typography>
          </Paper>
        </Grid>

        {/* 3. Total Teachers */}
        <Grid item xs={12} sm={6} md={2}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#E8F5E9' }}>
            <Typography variant="body2" sx={{ color: '#1B5E20', fontWeight: 500 }}>
              Total Teachers
            </Typography>
            <Typography variant="h4" sx={{ color: '#1B5E20', fontWeight: 700, mt: 1 }}>
              {formatNumber(stats.totalTeachers)}
            </Typography>
          </Paper>
        </Grid>

        {/* 4. PTR */}
        <Grid item xs={12} sm={6} md={2}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: ptrBg }}>
            <Typography variant="body2" sx={{ color: ptrColor, fontWeight: 500 }}>
              Pupil-Teacher Ratio
            </Typography>
            <Typography variant="h4" sx={{ color: ptrColor, fontWeight: 700, mt: 1 }}>
              {stats.ptr ? `${Math.round(stats.ptr)}:1` : 'N/A'}
            </Typography>
            {stats.ptr > 45 && (
              <Chip label="Above Target" size="small" color="warning" sx={{ mt: 0.5 }} />
            )}
          </Paper>
        </Grid>

        {/* 5. GPI */}
        <Grid item xs={12} sm={6} md={2}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: gpiBg }}>
            <Typography variant="body2" sx={{ color: gpiColor, fontWeight: 500 }}>
              Gender Parity Index
            </Typography>
            <Typography variant="h4" sx={{ color: gpiColor, fontWeight: 700, mt: 1 }}>
              {stats.gpi !== null ? stats.gpi.toFixed(2) : 'N/A'}
            </Typography>
          </Paper>
        </Grid>

        {/* 6. GPS Coverage */}
        <Grid item xs={12} sm={6} md={2}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: gpsBg }}>
            <Typography variant="body2" sx={{ color: gpsColor, fontWeight: 500 }}>
              GPS Coverage
            </Typography>
            <Typography variant="h4" sx={{ color: gpsColor, fontWeight: 700, mt: 1 }}>
              {gpsCoverage.toFixed(0)}%
            </Typography>
            <Typography variant="caption" sx={{ color: gpsColor }}>
              {stats.withGPS} / {stats.total} schools
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* ===== Row 2: Schools by District + Level Distribution ===== */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Left: Schools by District stacked bar */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              Schools by District
            </Typography>
            <Box sx={{ height: 350 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={districtLevelData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="district"
                    tick={{ fontSize: 11 }}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    tickFormatter={(v: string) => truncate(v, 12)}
                  />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  {LEVEL_KEYS.map((level) => (
                    <Bar
                      key={level}
                      dataKey={level}
                      stackId="levels"
                      fill={LEVEL_COLORS[level]}
                      name={level}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        {/* Right: School Level Distribution donut */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              School Level Distribution
            </Typography>
            <Box sx={{ height: 350 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={levelPieData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    label={({ name, percent }) =>
                      `${name} ${(percent * 100).toFixed(0)}%`
                    }
                  >
                    {levelPieData.map((entry) => (
                      <Cell
                        key={entry.name}
                        fill={LEVEL_COLORS[entry.name] || LEVEL_COLORS.Unknown}
                      />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: number) => formatNumber(value)} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* ===== Row 3: School Type + Top 10 Enrollment ===== */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Left: School Type Distribution donut */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              School Type Distribution
            </Typography>
            <Box sx={{ height: 350 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={typePieData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    label={({ name, percent }) =>
                      `${name} ${(percent * 100).toFixed(0)}%`
                    }
                  >
                    {typePieData.map((entry, idx) => (
                      <Cell key={entry.name} fill={typeColors[idx]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: number) => formatNumber(value)} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        {/* Right: Top 10 Schools by Enrollment horizontal bar */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              Top 10 Schools by Enrollment
            </Typography>
            <Box sx={{ height: 350 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={top10Schools} layout="vertical" margin={{ left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis
                    type="category"
                    dataKey="name"
                    tick={{ fontSize: 11 }}
                    width={130}
                  />
                  <Tooltip />
                  <Bar dataKey="enrollment" name="Enrollment">
                    {top10Schools.map((entry, idx) => (
                      <Cell
                        key={idx}
                        fill={LEVEL_COLORS[entry.level] || LEVEL_COLORS.Unknown}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* ===== Row 4: PTR by District + Gender Enrollment ===== */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Left: PTR by District */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              PTR by District
            </Typography>
            <Box sx={{ height: 350 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={ptrByDistrict}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="district"
                    tick={{ fontSize: 11 }}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    tickFormatter={(v: string) => truncate(v, 12)}
                  />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <ReferenceLine
                    y={40}
                    label={{ value: 'Target', position: 'right', fontSize: 11 }}
                    stroke="#D32F2F"
                    strokeDasharray="4 4"
                  />
                  <Bar dataKey="ptr" name="PTR">
                    {ptrByDistrict.map((entry, idx) => {
                      let color = '#388E3C';
                      if (entry.ptr > 60) color = '#D32F2F';
                      else if (entry.ptr > 40) color = '#FF9800';
                      return <Cell key={idx} fill={color} />;
                    })}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        {/* Right: Gender Enrollment by District */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              Gender Enrollment by District
            </Typography>
            <Box sx={{ height: 350 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={genderByDistrict}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="district"
                    tick={{ fontSize: 11 }}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    tickFormatter={(v: string) => truncate(v, 12)}
                  />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="male" name="Male" fill="#1976D2" />
                  <Bar dataKey="female" name="Female" fill="#E91E63" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardView;
